import React, { useCallback, useEffect, useMemo, useState } from "react";
import useAssetJobFilterAuthor from "domains/assets/hooks/useAssetAuthor";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import HeaderFilterAuthor from "domains/file-manager/components/FileManagerV2/FmHeaderFilterAuthor";
import HeaderFilterDate, {
  FmHeaderFilterDateProps,
} from "domains/file-manager/components/FileManagerV2/FmHeaderFilterDate";
import HeaderFilterTags from "domains/file-manager/components/FileManagerV2/FmHeaderFilterTags";
import { mapFileModelsToFmFileModels } from "domains/file-manager/utils/mapFmFileToFile";
import { ModelCapability } from "domains/models/constants/capabilities";
import {
  MODEL_FAMILIES,
  ModelFamily,
  ModelTag,
} from "domains/models/constants/tags";
import useAllModels from "domains/models/hooks/useAllModels";
import useFrontFiltersToBack from "domains/models/hooks/useFrontFiltersToBack";
import {
  getIsModelFluxDev,
  getIsModelFluxPro,
  getIsModelFluxPro1_1,
  getIsModelFluxPro1_1Ultra,
  getIsModelGptImage,
} from "domains/models/utils";
import {
  getApiCapabilities,
  getApiCapabilitiesFromFilter,
} from "domains/models/utils/capabilities";
import FilterModelCapabilities from "domains/search/components/SearchModal/FilterModelCapabilities";
import FilterModels from "domains/search/components/SearchModal/FilterModels";
import FilterModelType, {
  ModelType,
} from "domains/search/components/SearchModal/FilterModelType";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Icon from "domains/ui/components/Icon";
import MenuListMultipleChoice from "domains/ui/components/Menu/MenuListMultipleChoice";
import ScenarioInput from "domains/ui/components/ScenarioInput";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

import { UseAllModelsArgs } from "./useAllModels";

export type UseAllModelsWithFiltersArgs = UseAllModelsArgs & {
  variant?:
    | "image"
    | "canvas"
    | "canvas-live"
    | "texture"
    | "3d"
    | "video"
    | "all";
  isIgnoringFilters?: boolean;
};

export default function useAllModelsWithFilters(
  args: UseAllModelsWithFiltersArgs | undefined
) {
  const {
    variant = "all",
    privacyFilter,
    isIgnoringFilters = false,
    ...allModelsArgs
  } = args ?? {};
  const isPersistedState = !["texture", "video", "3d"].includes(variant ?? "");

  const { selectedTeam, selectedProject } = useTeamContext();
  const [search, setSearch] = useState<string>("");
  const [families, setFamilies] = usePersistedState<ModelFamily[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_FAMILIES : undefined,
    {
      defaultValue: [],
    }
  );
  const [type, setType] = usePersistedState<ModelType | undefined>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_TYPES : undefined,
    {
      defaultValue: undefined,
    }
  );
  const [dateRange, setDateRange] = useState<
    FmHeaderFilterDateProps["value"] | undefined
  >();
  const { authorQueryArgs, fmHeaderFilterAuthorProps } =
    useAssetJobFilterAuthor({ avoidPersistedState: true });
  const [statuses, setStatuses] = usePersistedState<
    GetModelsByModelIdApiResponse["model"]["status"][]
  >(isPersistedState ? PersistedStateKey.MODELS_FILTER_STATUSES : undefined, {
    defaultValue: [],
  });
  const [tags, setTags] = usePersistedState<ModelTag[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_TAGS : undefined,
    {
      defaultValue: [],
    }
  );
  const [conceptIds, setConceptIds] = usePersistedState<string[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_CONCEPT_IDS : undefined,
    {
      defaultValue: [],
    }
  );
  const [capabilities, setCapabilities] = usePersistedState<ModelCapability[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_CAPABILITIES : undefined,
    {
      defaultValue: [],
    }
  );
  const [privacy, setPrivacy] = useState<
    GetModelsByModelIdApiResponse["model"]["privacy"][]
  >([]);
  const [facets, setFacets] = useState<{
    tags: Record<string, number>;
    types: Record<string, number>;
    conceptIds: Record<string, number>;
    authorIds: Record<string, number>;
  }>({
    tags: {},
    types: {},
    conceptIds: {},
    authorIds: {},
  });

  const isCanvas = variant === "canvas" || variant === "canvas-live";

  const fmHeaderFilterDateProps = useMemo(
    () => ({ value: dateRange, onChange: setDateRange }),
    [dateRange, setDateRange]
  );

  const authorsFilter = useMemo(() => {
    return authorQueryArgs?.authorId ? [authorQueryArgs.authorId] : undefined;
  }, [authorQueryArgs?.authorId]);

  const isMultiSelectDisabled = !_.isEqual(families, ["private"]);

  const customFilter = useCallback(
    (model: GetModelsByModelIdApiResponse["model"]) => {
      if (
        isCanvas &&
        (getIsModelFluxPro(model) ||
          getIsModelFluxPro1_1(model) ||
          getIsModelFluxPro1_1Ultra(model) ||
          getIsModelGptImage(model) ||
          (variant === "canvas-live" && getIsModelFluxDev(model)))
      ) {
        return false;
      }

      if (model.privacy === "unlisted" && !privacy.includes("unlisted")) {
        return false;
      }

      if (privacy.length && !privacy.includes(model.privacy)) {
        return false;
      }

      if (families.length === 0) {
        return true;
      }
      if (
        families.includes("private") &&
        model?.ownerId === selectedProject.id
      ) {
        return true;
      }

      const hasFoundation =
        families.length === 0 || families.includes("foundation");
      const hasPlatform =
        families.length === 0 || families.includes("platform");
      const tags = model.tags.map((tag) => tag.toLowerCase());
      const familyTags = [
        ...(hasFoundation ? ["foundation", "sc:foundation"] : []),
        ...(hasPlatform ? ["signature", "sc:signature"] : []),
      ];
      return _.intersection(tags, familyTags).length > 0;
    },
    [families, isCanvas, privacy, selectedProject.id, variant]
  );

  const { types: typesFilter, sources: sourcesFilter } = useFrontFiltersToBack({
    type,
  });

  const capabilitiesFilter = useMemo(() => {
    if (variant !== "all" && capabilities.length === 0) {
      return getApiCapabilities(
        variant === "canvas" || variant === "canvas-live" ? "image" : variant
      );
    }

    return getApiCapabilitiesFromFilter(capabilities);
  }, [variant, capabilities]);

  const filterCount = isIgnoringFilters
    ? 0
    : (families.length > 0 ? 1 : 0) +
      (type !== undefined ? 1 : 0) +
      (statuses.length > 0 ? 1 : 0) +
      (authorsFilter ? 1 : 0) +
      (tags.length > 0 ? 1 : 0) +
      (conceptIds.length > 0 ? 1 : 0) +
      (privacy.length > 0 ? 1 : 0) +
      (capabilitiesFilter.length > 0 ? 1 : 0) +
      (fmHeaderFilterDateProps.value ? 1 : 0) +
      (search ? 1 : 0);

  const { files, ...allModelsResult } = useAllModels(
    args
      ? {
          ...allModelsArgs,
          ...(isIgnoringFilters
            ? { privacyFilter }
            : {
                typesFilter,
                tagsFilter: tags,
                statusesFilter: statuses,
                conceptsFilter: conceptIds,
                sourcesFilter,
                authorsFilter,
                capabilitiesFilter,
                dateRange: fmHeaderFilterDateProps.value,
                privacyFilter:
                  privacyFilter ??
                  (() => {
                    if (
                      families.length > 0 &&
                      _.difference(families, ["platform", "foundation"])
                        .length === 0
                    ) {
                      return "public";
                    } else if (
                      families.length === 1 &&
                      families.includes("private")
                    ) {
                      return "private";
                    } else {
                      return undefined;
                    }
                  })(),
                searchTerm: search,
                customFilter,
              }),
        }
      : undefined
  );

  const fmFiles = useMemo(() => mapFileModelsToFmFileModels(files), [files]);

  const categoryOptions = useMemo(
    () =>
      MODEL_FAMILIES.filter(
        (family) => privacyFilter !== "public" || family.id !== "private"
      ).map((family) => ({
        value: family.id,
        label: family.label,
        dataTestId: `model-selection-family-${family.id}`,
      })),
    [privacyFilter]
  );

  // ----------------------------------

  useEffect(() => {
    function calculateFacets() {
      setFacets((prevFacets) => {
        function getPreviousFacet(key: keyof typeof facets) {
          return Object.keys(prevFacets[key]).reduce((acc, item) => {
            acc[item] = 0;
            return acc;
          }, {} as Record<string, number>);
        }

        return files.reduce(
          (acc, file) => {
            file.meta.tags.forEach((tag) => {
              if (
                tag !== "sc:foundation" &&
                tag !== "sc:signature" &&
                tag !== "signature" &&
                tag !== "foundation"
              ) {
                acc.tags[tag] = (acc.tags[tag] ?? 0) + 1;
              }
            });
            acc.types[file.meta.type] = (acc.types[file.meta.type] ?? 0) + 1;
            file.meta.concepts?.forEach((concept) => {
              acc.conceptIds[concept.modelId] =
                (acc.conceptIds[concept.modelId] ?? 0) + 1;
            });
            if (file.meta.authorId) {
              acc.authorIds[file.meta.authorId] =
                (acc.authorIds[file.meta.authorId] ?? 0) + 1;
            }
            return acc;
          },
          {
            tags: getPreviousFacet("tags"),
            types: getPreviousFacet("types"),
            conceptIds: getPreviousFacet("conceptIds"),
            authorIds: getPreviousFacet("authorIds"),
          }
        );
      });
    }
    const timeout = setTimeout(calculateFacets, 1_000);
    return () => clearTimeout(timeout);
  }, [files]);

  // ----------------------------------

  return {
    files: fmFiles,
    ...allModelsResult,
    filterCount,
    isMultiSelectDisabled,
    topBarFilterContent: isIgnoringFilters ? undefined : (
      <>
        <ScenarioInput
          value={search}
          setValue={setSearch}
          placeholder="Search"
          leftComponent={<Icon id="Ui/Search" color="textTertiary" />}
          dataTestId="file-manager-selection-modal-search-input-model"
          containerProps={{
            w: "250px",
            flexShrink: 0,
          }}
        />

        <MenuListMultipleChoice
          label="Category"
          options={categoryOptions}
          selectedValues={families}
          setSelectedValues={setFamilies}
          withoutSearch
          dataTestId="model-selection-family-category"
        />
        <FilterModelType value={type} setValue={setType} models={files} />
        <FilterModelCapabilities
          variant={
            variant === "canvas" || variant === "canvas-live"
              ? "image"
              : variant
          }
          value={capabilities}
          setValue={setCapabilities}
          models={files}
        />
        {!!fmHeaderFilterAuthorProps && privacyFilter !== "public" && (
          <HeaderFilterAuthor
            {...fmHeaderFilterAuthorProps}
            facets={facets.authorIds}
          />
        )}
        {privacyFilter !== "public" && (
          <HeaderFilterDate {...fmHeaderFilterDateProps} />
        )}
        <HeaderFilterTags
          setValue={setTags}
          value={tags}
          facets={facets.tags}
        />
        <FilterModels
          label="Components"
          value={conceptIds}
          setValue={setConceptIds}
          facets={facets.conceptIds}
        />

        {privacyFilter !== "public" && (
          <MenuListMultipleChoice
            label="Status"
            options={[
              {
                value: "trained",
                label: "Ready",
              },
              {
                value: "training",
                label: "Training",
              },
              {
                value: "new",
                label: "Draft",
              },
            ]}
            selectedValues={statuses}
            setSelectedValues={setStatuses}
            withoutSearch
          />
        )}

        {privacyFilter !== "public" && selectedTeam.isPublic && (
          <MenuListMultipleChoice
            label="Privacy"
            options={[
              {
                value: "public",
                label: "Public",
              },
              {
                value: "private",
                label: "Private",
              },
              {
                value: "unlisted",
                label: "Unlisted",
              },
            ]}
            selectedValues={privacy}
            setSelectedValues={setPrivacy}
            withoutSearch
          />
        )}
      </>
    ),
  };
}
